import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { http, inr, mailtoLink, requestErrorMessage } from "@/lib/erp";
import { useWhatsAppSender } from "@/context/WhatsAppSendContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Wallet, Phone, Mail, MapPin, CreditCard, Printer, MessageCircle, Link2, Link2Off } from "lucide-react";
import PrintBrandHeader from "@/components/PrintBrandHeader";
import { toast } from "sonner";

export default function CustomerDetail() {
  const requestWhatsAppSend = useWhatsAppSender();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [company, setCompany] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [companyError, setCompanyError] = useState("");
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try { const r = await http.get(`/customers/${id}/ledger`); setData(r.data); setLoadError(""); }
    catch (e) { setLoadError(requestErrorMessage(e, "load customer statement")); }
  }, [id]);
  useEffect(() => { setData(null); setLoadError(""); load(); }, [id, load]);
  useEffect(() => {
    http.get("/settings").then((r) => { setCompany(r.data); setCompanyError(""); })
      .catch((e) => setCompanyError(requestErrorMessage(e, "load company branding")));
  }, []);
  if (!data) return <div className="p-8 space-y-3 text-muted-foreground">
    {loadError ? <><div role="alert" className="text-rose-700">{loadError}</div><Button variant="outline" onClick={load}>Retry</Button></> : "Loading…"}
  </div>;
  const c = data.customer;

  const stmtMsg = `Dear ${c.name},\n\nAccount statement:\nTotal Billed: ${inr(data.total_debit)}\nCollected: ${inr(data.total_credit)}\nOutstanding: ${inr(data.outstanding)}\n\nThank you for your business.`;
  const ensureShare = async () => {
    const { data } = await http.post(`/customers/${id}/share`);
    return `${window.location.origin}/p/statement/${data.token}`;
  };
  const copyLink = async () => {
    try {
      const url = await ensureShare();
      if (!navigator.clipboard) {
        toast.warning("Clipboard access is unavailable", { description: url });
      } else {
        try { await navigator.clipboard.writeText(url); toast.success("Statement link copied", { description: url }); }
        catch (e) { toast.warning(requestErrorMessage(e, "copy statement link"), { description: url }); }
      }
      load();
    } catch (e) { toast.error(requestErrorMessage(e, "create statement link")); }
  };
  const revokeLink = async () => {
    if (!window.confirm("Invalidate the statement link? Anyone holding the old link will lose access.")) return;
    try { await http.delete(`/customers/${id}/share`); toast.success("Statement link invalidated"); load(); }
    catch (e) { toast.error(requestErrorMessage(e, "invalidate statement link")); }
  };
  const shareWhatsApp = async () => {
    const companyId = localStorage.getItem("vg_company");
    let url = "";
    try { url = await ensureShare(); load(); }
    catch (e) { toast.warning(`${requestErrorMessage(e, "create statement link")}. Sharing statement details without a link.`); }
    const msg = url ? `${stmtMsg}\n\nView statement: ${url}` : stmtMsg;
    requestWhatsAppSend({ phone: c.phone, message: msg, title: `Account statement — ${c.name}`, companyId });
  };
  const shareEmail = () => { if (!c.email) return toast.error("No email saved for this customer"); window.location.href = mailtoLink(c.email, `Account Statement — ${c.name}`, stmtMsg); };

  return (
    <div id="print-area" className="p-4 sm:p-6 lg:p-8 space-y-6">
      <PrintBrandHeader company={company} title="Account Statement" subtitle={c.name} />
      {loadError && <div role="alert" className="no-print flex items-center justify-between gap-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3"><span>{loadError}</span><Button size="sm" variant="outline" onClick={load}>Retry</Button></div>}
      {companyError && <div role="status" className="no-print text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">{companyError}. Statement content is still available.</div>}
      <div className="flex items-center justify-between flex-wrap gap-3 no-print">
        <button onClick={() => navigate("/parties/customers")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to customers
        </button>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={shareWhatsApp} data-testid="ledger-whatsapp-button"><MessageCircle className="h-4 w-4 mr-1 text-emerald-600" /> WhatsApp</Button>
          <Button size="sm" variant="outline" onClick={shareEmail} data-testid="ledger-email-button"><Mail className="h-4 w-4 mr-1 text-brand-600" /> Email</Button>
          <Button size="sm" variant="outline" onClick={copyLink} data-testid="statement-copy-link-button"><Link2 className="h-4 w-4 mr-1" /> Copy Link</Button>
          {data.has_share_link && <Button size="sm" variant="outline" className="text-rose-600" onClick={revokeLink} data-testid="statement-revoke-link-button"><Link2Off className="h-4 w-4 mr-1" /> Invalidate Link</Button>}
          <Button size="sm" variant="print" onClick={() => window.print()} data-testid="ledger-print-button"><Printer className="h-4 w-4 mr-1" /> Print</Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="p-6">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-primary/10 text-primary grid place-items-center text-lg font-bold">{c.name[0]}</div>
            <div>
              <h1 className="font-heading text-xl font-bold">{c.name}</h1>
              <span className="text-xs bg-slate-100 px-2 py-0.5 rounded">{c.customer_type}</span>
            </div>
          </div>
          <div className="mt-5 space-y-2.5 text-sm">
            {c.phone && <div className="flex items-center gap-2 text-slate-600"><Phone className="h-4 w-4" />{c.phone}</div>}
            {c.email && <div className="flex items-center gap-2 text-slate-600"><Mail className="h-4 w-4" />{c.email}</div>}
            {c.gstin && <div className="flex items-center gap-2 text-slate-600"><CreditCard className="h-4 w-4" /><span className="font-mono">{c.gstin}</span></div>}
            {c.billing_address && <div className="flex items-start gap-2 text-slate-600"><MapPin className="h-4 w-4 mt-0.5" />{c.billing_address}</div>}
          </div>
        </Card>

        <Card className="p-6 lg:col-span-2">
          <div className="grid grid-cols-3 gap-4">
            <div><div className="text-xs uppercase tracking-wider text-muted-foreground">Total Billed</div><div className="font-heading text-xl font-bold mt-1 tabular">{inr(data.total_debit)}</div></div>
            <div><div className="text-xs uppercase tracking-wider text-muted-foreground">Collected</div><div className="font-heading text-xl font-bold mt-1 tabular text-emerald-600">{inr(data.total_credit)}</div></div>
            <div><div className="text-xs uppercase tracking-wider text-muted-foreground">Outstanding</div><div className="font-heading text-xl font-bold mt-1 tabular text-rose-600">{inr(data.outstanding)}</div></div>
          </div>
          <div className="mt-4 flex gap-2">
            <Button size="sm" onClick={() => navigate(`/sales/invoices/new?customer=${id}`)} data-testid="new-invoice-for-customer">New Invoice</Button>
            <Button size="sm" variant="outline" onClick={() => navigate(`/payments?customer=${id}`)}><Wallet className="h-4 w-4 mr-1" /> Record Payment</Button>
          </div>
        </Card>
      </div>

      <Card className="p-0 overflow-hidden">
        <div className="p-4 border-b"><h3 className="font-heading font-semibold">Ledger / Statement</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3 font-medium">Date</th><th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Reference</th>
                <th className="px-4 py-3 font-medium text-right">Debit</th>
                <th className="px-4 py-3 font-medium text-right">Credit</th>
                <th className="px-4 py-3 font-medium text-right">Balance</th>
              </tr>
            </thead>
            <tbody>
              {data.entries.map((e, i) => (
                <tr key={i} className="border-t" data-testid={`ledger-row-${i}`}>
                  <td className="px-4 py-3">{e.date}</td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded ${e.type === "Invoice" ? "bg-brand-50 text-brand-700" : "bg-emerald-50 text-emerald-700"}`}>{e.type}</span></td>
                  <td className="px-4 py-3 font-mono">{e.ref}</td>
                  <td className="px-4 py-3 text-right font-mono">{e.debit ? inr(e.debit) : "—"}</td>
                  <td className="px-4 py-3 text-right font-mono text-emerald-600">{e.credit ? inr(e.credit) : "—"}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold">{inr(e.balance)}</td>
                </tr>
              ))}
              {data.entries.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">No transactions yet</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
